import { historyGetIsChangedStyle } from '@utils/historyGetIsChangedStyle';
import { AgTooltipHeaderComponent } from 'components/AgGrid/AgTooltipHeaderComponent';
import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { UerChapter, UerClassifier } from 'constantsProject';
import type { CatalogUerBaseCostHistoryData, ChapterEnum, UerClassifierEnum } from 'gql/graphql';
import { UerBaseCostChangeType } from 'gql/graphql';
import { CollapseHeaderButton } from 'pages/Catalogs/Common/EstimateBasisModal/components/CollapseHeaderButton';
import { formatDateToLocale } from 'utils';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';

import {
    catalogUERBaseCostHistoryChapterFilterGql,
    catalogUERBaseCostHistoryCodeFilterGql,
    catalogUERBaseCostHistoryGroupFullNameFilterGql,
    catalogUERBaseCostHistoryJustificationFilterGql,
    catalogUERBaseCostHistoryNameFilterGql,
    catalogUERBaseCostHistoryUerClassifierFilterGql,
    catalogUERBaseCostHistoryWorkScopeFilterGql,
} from '../catalogUerBaseCostHistoryQueries';
import { MenuCellRenderer } from '../components';
import { numberFormatter, groupFullNameFilterWhereGetter, historyFilterColumns } from '../helpers';

const collapseKeys = [
    'catalogUERBaseCostChange.ozp',
    'catalogUERBaseCostChange.emm',
    'catalogUERBaseCostChange.zpm',
    'catalogUERBaseCostChange.mtr',
    'catalogUERBaseCostChange.eqp',
    'catalogUERBaseCostChange.trn',
    'catalogUERBaseCostChange.pir',
    'catalogUERBaseCostChange.nr',
    'catalogUERBaseCostChange.sp',
];
const defaultCollapsibleWidth = 100;

interface IUseHistoryColumnsProps {
    handlerFilterChange: ({
        filterType,
        value,
    }: {
        filterType: 'columnFilters';
        value: Record<string, any[]> | null;
    }) => void;
    catalogId: string | undefined;
    tableRdy: boolean;
    dateFilterValues: {
        endDate: string | undefined;
        startDate: string | undefined;
    };
}

export const useHistoryColumns = ({
    catalogId,
    handlerFilterChange,
    tableRdy,
    dateFilterValues,
}: IUseHistoryColumnsProps): (ColDef<CatalogUerBaseCostHistoryData> | ColGroupDef<CatalogUerBaseCostHistoryData>)[] => {
    const filterVariables = useMemo(
        () => ({
            catalogUniqueId: catalogId,
            startDate: dateFilterValues.startDate,
            endDate: dateFilterValues.endDate,
        }),
        [catalogId, dateFilterValues.endDate, dateFilterValues.startDate],
    );

    const onFilterChange = useCallback(
        ({ fieldName, checkedValues }: { fieldName: string; checkedValues: any[] }) => {
            handlerFilterChange({ filterType: 'columnFilters', value: { [fieldName]: checkedValues } });
        },
        [handlerFilterChange],
    );

    return [
        {
            headerName: '',
            field: 'menu' as any,
            width: 50,
            resizable: false,
            cellRenderer: MenuCellRenderer,
            cellRendererParams: {
                catalogId,
            },
        },
        {
            headerName: 'Дата изменения',
            field: 'updateUerDate',
            width: 150,
            valueGetter: ({ data }: { data: CatalogUerBaseCostHistoryData | undefined }) =>
                data?.isHeadLine ? data?.uerBaseCostCode : formatDateToLocale(data?.updateUerDate),
            colSpan: ({ data }: { data: CatalogUerBaseCostHistoryData | undefined }) => (data?.isHeadLine ? 6 : 1),
        },
        {
            headerName: 'Подраздел',
            field: historyFilterColumns.groupFullName,
            width: 200,
            cellClass: historyGetIsChangedStyle(),
            valueGetter: ({ data }) =>
                `${data?.catalogUERBaseCostChange?.groupCode ?? ''} ${
                    data?.catalogUERBaseCostChange?.groupFullName ?? ''
                }`,
            ...(tableRdy && {
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    customWhereVariablesGetter: groupFullNameFilterWhereGetter,
                    changeFilterHandler: onFilterChange,
                    dataAccessor: 'catalogUERBaseCostHistoryGroupFullNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Подраздел',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueGetter: ({
                                data,
                            }: {
                                data:
                                    | {
                                          value: {
                                              groupCode: string;
                                              groupName: string;
                                          };
                                      }
                                    | undefined;
                            }) => {
                                if (!data?.value?.groupCode && !data?.value?.groupName) {
                                    return 'Нет значения';
                                }

                                return `${data?.value?.groupCode ?? ''} ${data?.value?.groupName ?? ''}`;
                            },
                        },
                    ],
                    query: catalogUERBaseCostHistoryGroupFullNameFilterGql,
                },
            }),
        },
        {
            headerName: 'Шифр',
            field: historyFilterColumns.code,
            width: 200,
            cellClass: historyGetIsChangedStyle(),
            ...(tableRdy && {
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    changeFilterHandler: onFilterChange,
                    dataAccessor: 'catalogUERBaseCostHistoryCodeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: catalogUERBaseCostHistoryCodeFilterGql,
                },
            }),
        },
        {
            headerName: 'Наименование',
            field: historyFilterColumns.name,
            flex: 1,
            minWidth: 300,
            cellClass: historyGetIsChangedStyle(),
            ...(tableRdy && {
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    changeFilterHandler: onFilterChange,
                    dataAccessor: 'catalogUERBaseCostHistoryNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: catalogUERBaseCostHistoryNameFilterGql,
                },
            }),
        },
        {
            headerName: 'Ед.изм.',
            field: 'catalogUERBaseCostChange.unit',
            width: 90,
            cellClass: historyGetIsChangedStyle(),
        },
        {
            headerName: 'Стоимость единицы в базисном уровне цен',
            headerGroupComponent: AgTooltipHeaderComponent,
            headerGroupComponentParams: {
                displayName: 'Стоимость единицы в базисном уровне цен',
            },
            width: 120,
            children: [
                {
                    headerName: 'Всего',
                    field: 'catalogUERBaseCostChange.unitCost',
                    headerComponent: CollapseHeaderButton,
                    headerComponentParams: {
                        collapseKeys,
                    },
                    valueFormatter: numberFormatter,
                    width: defaultCollapsibleWidth,
                    cellClass: historyGetIsChangedStyle({
                        extraClass: 'ag-right-aligned-cell',
                    }),
                },
                {
                    headerName: 'ОЗП',
                    field: 'catalogUERBaseCostChange.ozp',
                    valueFormatter: numberFormatter,
                    width: defaultCollapsibleWidth,
                    cellClass: historyGetIsChangedStyle({
                        extraClass: 'ag-right-aligned-cell',
                    }),
                },
                {
                    headerName: 'ЭММ',
                    field: 'catalogUERBaseCostChange.emm',
                    valueFormatter: numberFormatter,
                    width: defaultCollapsibleWidth,
                    cellClass: historyGetIsChangedStyle({
                        extraClass: 'ag-right-aligned-cell',
                    }),
                },
                {
                    headerName: 'ЗПМ',
                    field: 'catalogUERBaseCostChange.zpm',
                    valueFormatter: numberFormatter,
                    width: defaultCollapsibleWidth,
                    cellClass: historyGetIsChangedStyle({
                        extraClass: 'ag-right-aligned-cell',
                    }),
                },
                {
                    headerName: 'МТ',
                    field: 'catalogUERBaseCostChange.mtr',
                    valueFormatter: numberFormatter,
                    width: defaultCollapsibleWidth,
                    cellClass: historyGetIsChangedStyle({
                        extraClass: 'ag-right-aligned-cell',
                    }),
                },
                {
                    headerName: 'Оборудование',
                    field: 'catalogUERBaseCostChange.eqp',
                    valueFormatter: numberFormatter,
                    width: 150,
                    cellClass: historyGetIsChangedStyle({
                        extraClass: 'ag-right-aligned-cell',
                    }),
                },
                {
                    headerName: 'Перевозка',
                    field: 'catalogUERBaseCostChange.trn',
                    valueFormatter: numberFormatter,
                    width: 130,
                    cellClass: historyGetIsChangedStyle({
                        extraClass: 'ag-right-aligned-cell',
                    }),
                },
                {
                    headerName: 'ПИР',
                    field: 'catalogUERBaseCostChange.pir',
                    valueFormatter: numberFormatter,
                    width: defaultCollapsibleWidth,
                    cellClass: historyGetIsChangedStyle({
                        extraClass: 'ag-right-aligned-cell',
                    }),
                },
                {
                    headerName: 'НР',
                    field: 'catalogUERBaseCostChange.nr',
                    valueFormatter: numberFormatter,
                    width: defaultCollapsibleWidth,
                    cellClass: historyGetIsChangedStyle({
                        extraClass: 'ag-right-aligned-cell',
                    }),
                },
                {
                    headerName: 'СП',
                    field: 'catalogUERBaseCostChange.sp',
                    valueFormatter: numberFormatter,
                    width: defaultCollapsibleWidth,
                    cellClass: historyGetIsChangedStyle({
                        extraClass: 'ag-right-aligned-cell',
                    }),
                },
            ],
        },
        {
            headerName: 'Глава в расчете',
            field: historyFilterColumns.chapter,
            width: 125,
            valueFormatter: ({ value }) => UerChapter[value] ?? '',
            cellClass: historyGetIsChangedStyle(),
            ...(tableRdy && {
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    withSearch: false,
                    variables: filterVariables,
                    changeFilterHandler: onFilterChange,
                    dataAccessor: 'catalogUERBaseCostHistoryChapterFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Глава в расчете',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueGetter: ({ data }: { data?: { value: ChapterEnum } }) =>
                                data?.value ? UerChapter[data?.value] : 'Нет значения',
                        },
                    ],
                    query: catalogUERBaseCostHistoryChapterFilterGql,
                },
            }),
        },
        {
            headerName: 'Тип расценки',
            field: historyFilterColumns.uerClassifier,
            width: 152,
            cellRenderer: ({ value }: { value: string }) => UerClassifier[value] || '',
            cellClass: historyGetIsChangedStyle(),
            ...(tableRdy && {
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    withSearch: false,
                    variables: filterVariables,
                    changeFilterHandler: onFilterChange,
                    dataAccessor: 'catalogUERBaseCostHistoryUerClassifierFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Тип расценки',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueGetter: ({ data }: { data?: { value: UerClassifierEnum } }) =>
                                data?.value ? UerClassifier[data?.value] : 'Нет значения',
                        },
                    ],
                    query: catalogUERBaseCostHistoryUerClassifierFilterGql,
                },
            }),
        },
        {
            headerName: 'Вид строительства',
            field: 'catalogUERBaseCostChange.constructionTypes',
            width: 200,
            valueGetter: ({ data }: { data?: CatalogUerBaseCostHistoryData }) =>
                data?.catalogUERBaseCostChange?.constructionTypes
                    ?.map((item: { name: string }) => item.name)
                    .join('; ') ?? '',
            cellRenderer: ({ value }: { value: string }) => <div className="trunCate">{value}</div>,
            cellClass: historyGetIsChangedStyle({
                customKey: 'catalogUERBaseCostChange.constructionTypeCng',
            }),
        },
        {
            headerName: 'Состав работ',
            field: historyFilterColumns.hasWorkScopesHistory,
            width: 150,
            valueGetter: ({ data }: { data?: CatalogUerBaseCostHistoryData }) =>
                data?.catalogUERBaseCostChange?.hasWorkScopesHistory ? 'Изменен' : '',
            ...(tableRdy && {
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    withSearch: false,
                    variables: filterVariables,
                    changeFilterHandler: onFilterChange,
                    dataAccessor: 'catalogUERBaseCostHistoryWorkScopeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Состав работ',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueGetter: ({ data }: { data?: { value: UerBaseCostChangeType } }) =>
                                data?.value === UerBaseCostChangeType.Updated ? 'Изменен' : 'Нет значения',
                        },
                    ],
                    query: catalogUERBaseCostHistoryWorkScopeFilterGql,
                },
            }),
        },
        {
            headerName: 'Обоснование',
            field: historyFilterColumns.justification,
            width: 160,
            cellClass: historyGetIsChangedStyle(),
            ...(tableRdy && {
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    changeFilterHandler: onFilterChange,
                    dataAccessor: 'catalogUERBaseCostHistoryJustificationFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Обоснование',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: catalogUERBaseCostHistoryJustificationFilterGql,
                },
            }),
        },
    ];
};
